import * as React from 'react';
import tw from 'twin.macro';
import { ChevronUpIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { useIntercom } from 'react-use-intercom';
import { Controller, useForm } from 'react-hook-form';

import { BaseModal, ButtonV2, IconButton, RadioGroup, SlotPurchaseCard, Switch, Title } from '@restworld/ui-ds';
import { getAssetSrc, useIsFreemiumUser } from '@restworld/utils-common';
import { useIsEmployerAnAdmin } from '@restworld/feature-authentication';

import {
  useBuySlot,
  useFetchFlexibleSlotsPrices,
  useFetchFlexibleSlotsPricesLoading,
} from './product-description.hooks';
import {
  useIsShowOrganisationQueryLoading,
  useShowOrganizationData,
} from '../organisations/hooks/use-show-organisation-query';
import { useV2Router } from '../dashboard/hooks/use-v2-router';
import EmployerNoPermissionToBuySlotModal, {
  isEmployerNoPermissionToBuySlotModalOpenAtom,
} from '../employer-companies/modals/employer-no-permission-to-buy-slot-modal';
import { globalDiscountAtom } from '.';

const paymentProviderLogoMap = {
  visa: getAssetSrc('/utils-assets/visa.svg'),
  mastercard: getAssetSrc('/utils-assets/mastercard.svg'),
  maestro: getAssetSrc('/utils-assets/maestro.svg'),
  amex: getAssetSrc('/utils-assets/amex.svg'),
};

const showSubscriptionPurchaseShowcaseAtom = atom<boolean>(false);
const isAnnualSubscriptionAtom = atom<boolean>(false);
type SubscriptionPlanType = 'custom' | '3bundle' | '6bundle' | 'subscription' | 'trial';
export const slotNumberAtom = atom<number>(1);
export const activeSelectionSubscriptionItemAtom = atom<SubscriptionPlanType>('trial');

const useSubscriptionPurchaseShowcase = () => {
  const [showSubscriptionPurchaseShowcase, subscriptionPurchaseShowcaseToggler] = useAtom(
    showSubscriptionPurchaseShowcaseAtom
  );
  const [isAnnualSubscription, isAnnualSubscriptionToggler] = useAtom(isAnnualSubscriptionAtom);
  const [activeSelectionSubscriptionItem, setActiveSelectionSubscriptionItem] = useAtom(
    activeSelectionSubscriptionItemAtom
  );
  const setSlots = useSetAtom(slotNumberAtom);
  const isFreemiumUser = useIsFreemiumUser();

  const onClose = () => subscriptionPurchaseShowcaseToggler(false);

  React.useEffect(() => {
    setActiveSelectionSubscriptionItem(isAnnualSubscription ? 'subscription' : isFreemiumUser ? 'trial' : '3bundle');
    if (!isFreemiumUser && !isAnnualSubscription) setSlots(3);
  }, [isAnnualSubscription, setActiveSelectionSubscriptionItem, isFreemiumUser, setSlots]);

  return {
    showSubscriptionPurchaseShowcase,
    subscriptionPurchaseShowcaseToggler,
    isAnnualSubscription,
    isAnnualSubscriptionToggler,
    activeSelectionSubscriptionItem,
    setActiveSelectionSubscriptionItem,
    onClose,
  };
};

const CustomPurchaseCard = ({
  type,
  ctaLabel,
  onCtaClick,
  isCtaDisabled,
  isCtaLoading,
}: {
  type: 'custom' | 'subscription';
  ctaLabel?: string;
  onCtaClick?: () => void;
  isCtaDisabled?: boolean;
  isCtaLoading?: boolean;
}) => {
  const isLoading = useFetchFlexibleSlotsPricesLoading();
  const isAnnualSubscription = useAtomValue(isAnnualSubscriptionAtom);
  const [customSlotPurchaseNumber, changeCustomSlotPurchaseNumber] = React.useState<number>(1);
  const [activeSelectionSubscriptionItem, setActiveSelectionSubscriptionItem] = useAtom(
    activeSelectionSubscriptionItemAtom
  );
  const setSlotNumber = useSetAtom(slotNumberAtom);
  const globalDiscount = useAtomValue(globalDiscountAtom);
  const flexibleSlotsPrices = useFetchFlexibleSlotsPrices();
  const { t } = useTranslation('product-description-page');
  const getSlotPriceByBundle = React.useCallback(
    (slotNumber: number) => {
      const slotPrice = flexibleSlotsPrices.find((v) => v.no_of_slots === slotNumber)?.price * slotNumber;
      return slotPrice;
    },
    [flexibleSlotsPrices]
  );
  const subscriptionDesc = [
    `👩🏻‍💼 ${t('manager_available_all_year')}`,
    `👉 ${t('staff_searches_per_month')}`,
    `✅ ${t('custom_point_4')}`,
  ];
  const customDesc = [
    `👉 ${t('custom_point_1')}`,
    `🔑 ${t('custom_point_2')}`,
    `👩🏻‍💼 ${t('custom_point_3')}`,
    `✅ ${t('custom_point_4')}`,
  ];

  React.useEffect(() => {
    setSlotNumber(customSlotPurchaseNumber);
  }, [customSlotPurchaseNumber, setSlotNumber]);

  const getAnnualSubscriptionPricesBySlotNumber = React.useCallback((slotNumber: number) => {
    if (slotNumber >= 1 && slotNumber <= 3) return 249 * slotNumber;
    if (slotNumber >= 4 && slotNumber <= 9) return 229 * slotNumber;
    if (slotNumber >= 10) return 199 * slotNumber;
    return 0;
  }, []);

  const getPrice = React.useCallback(
    (slotNumber: number) =>
      isAnnualSubscription ? getAnnualSubscriptionPricesBySlotNumber(slotNumber) : getSlotPriceByBundle(slotNumber),
    [getAnnualSubscriptionPricesBySlotNumber, getSlotPriceByBundle, isAnnualSubscription]
  );

  return (
    <SlotPurchaseCard
      type={type}
      headerTitle={!isAnnualSubscription ? t('custom') : `${customSlotPurchaseNumber} ${t('slot_per_month')}`}
      description={type === 'custom' && t('custom_description')}
      isActive={activeSelectionSubscriptionItem === type}
      onClick={() => setActiveSelectionSubscriptionItem(type)}
      slotPrice={getPrice(customSlotPurchaseNumber)}
      onSlotAdd={() => changeCustomSlotPurchaseNumber((prev) => prev + 1)}
      onSlotMinus={() => changeCustomSlotPurchaseNumber((prev) => prev - 1)}
      slotNumber={customSlotPurchaseNumber}
      discountPrice={customSlotPurchaseNumber * getPrice(1)}
      discount={globalDiscount}
      bundlePrice={getPrice(customSlotPurchaseNumber)}
      slotPurchaseDescriptions={isAnnualSubscription ? subscriptionDesc : customDesc}
      loading={isLoading}
      ctaLabel={ctaLabel}
      onCtaClick={onCtaClick}
      isCtaDisabled={isCtaDisabled}
      isCtaLoading={isCtaLoading}
    />
  );
};

const PaymentProviderLogo = ({ paymentProvider }: { paymentProvider: keyof typeof paymentProviderLogoMap }) => (
  <Image
    src={paymentProviderLogoMap[paymentProvider]}
    alt={paymentProvider}
    width={23}
    height={16}
    tw="object-cover object-center"
  />
);
export const SubscriptionPurchaseHeader = ({ ...rest }) => {
  const { isAnnualSubscription, isAnnualSubscriptionToggler } = useSubscriptionPurchaseShowcase();
  const { t } = useTranslation('product-description-page');
  return (
    <div tw="pb-4 px-4 lg:px-0 justify-center items-center inline-flex flex-row gap-7 w-full" {...rest}>
      <Title
        textColor="neutral-200"
        fontSize="small"
        fontWeight="normal"
        content={t('save_with_the_annual_subscription')}
      />
      <Switch size="sm" onChange={() => isAnnualSubscriptionToggler((prev) => !prev)} checked={isAnnualSubscription} />
    </div>
  );
};
const SubscriptionPurchaseContentHeader = () => {
  const { t } = useTranslation('product-description-page');
  const isAnnualSubscription = useAtomValue(isAnnualSubscriptionAtom);

  return (
    <div tw="flex-col justify-start items-start gap-5 inline-flex w-full" id="subscription-purchase-content-header">
      <Title
        textColor="neutral-700"
        fontSize="base"
        fontWeight="semibold"
        content={isAnnualSubscription ? `${t('annual_subscription')}:` : `${t('choose_your_option')}:`}
      />
      {isAnnualSubscription && (
        <div tw="flex flex-col gap-2">
          <Title
            textColor="neutral-200"
            fontSize="small"
            fontWeight="normal"
            content={t('annual_subscription_guarantee')}
          />
          <Title
            textColor="neutral-200"
            fontSize="small"
            fontWeight="normal"
            content={t('ideal_solution_for_companies')}
          />
          <Title
            textColor="neutral-200"
            fontSize="small"
            fontWeight="normal"
            content={t('choose_slots_and_contact_sales')}
          />
        </div>
      )}
    </div>
  );
};

const BundleSlotPurchaseCard = ({
  slotNumber,
  isActive,
  onClick,
  ctaLabel,
  onCtaClick,
  isCtaDisabled,
  isCtaLoading,
}: {
  slotNumber: number;
  isActive: boolean;
  onClick: () => void;
  ctaLabel?: string;
  onCtaClick?: () => void;
  isCtaDisabled?: boolean;
  isCtaLoading?: boolean;
}) => {
  const flexibleSlotsPrices = useFetchFlexibleSlotsPrices();
  const isLoading = useFetchFlexibleSlotsPricesLoading();
  const { t } = useTranslation('product-description-page');

  const globalDiscount = useAtomValue(globalDiscountAtom);

  const getSlotPriceByBundle = React.useCallback(
    (slotNumber: number) => flexibleSlotsPrices.find((v) => v.no_of_slots === slotNumber)?.price * slotNumber,
    [flexibleSlotsPrices]
  );

  const bundleSlotPurchaseDescriptions = React.useMemo(
    () => [
      `${slotNumber === 6 ? '🤩' : '💡'} ${t('bundle_point_1', { count: slotNumber })}`,
      `⏳ ${t('bundle_point_2')}`,
      `📉 ${t('bundle_point_3')}`,
      `🔥 ${t('bundle_point_4')}`,
      `🔑 ${t('bundle_point_5')}`,
    ],
    [t, slotNumber]
  );

  const discountPrice = React.useMemo(() => slotNumber * getSlotPriceByBundle(1), [getSlotPriceByBundle, slotNumber]);

  const isDiscountPriceAndSlotPriceEqual = React.useMemo(() => {
    const slotPrice = getSlotPriceByBundle(slotNumber);
    return slotPrice === discountPrice;
  }, [discountPrice, getSlotPriceByBundle, slotNumber]);

  return (
    <SlotPurchaseCard
      slotNumber={slotNumber}
      headerTitle={`Pacchetto ${slotNumber} slot`}
      bundlePrice={getSlotPriceByBundle(slotNumber)}
      slotPrice={getSlotPriceByBundle(1)}
      discountPrice={!isDiscountPriceAndSlotPriceEqual ? discountPrice : null}
      type="bundle"
      discount={globalDiscount}
      isActive={isActive}
      onClick={onClick}
      slotPurchaseDescriptions={bundleSlotPurchaseDescriptions}
      loading={isLoading}
      isMostRequested={slotNumber === 3}
      ctaLabel={ctaLabel}
      onCtaClick={onCtaClick}
      isCtaDisabled={isCtaDisabled}
      isCtaLoading={isCtaLoading}
    />
  );
};

export const SubscriptionPurchaseContent = ({ ...rest }) => {
  const { isAnnualSubscription, activeSelectionSubscriptionItem, setActiveSelectionSubscriptionItem } =
    useSubscriptionPurchaseShowcase();
  const setSlotNumber = useSetAtom(slotNumberAtom);
  const isFreemiumUser = useIsFreemiumUser();
  const { t } = useTranslation('product-description-page');

  return (
    <div tw="flex-col justify-start items-center gap-5 inline-flex w-full" {...rest}>
      <SubscriptionPurchaseContentHeader />
      <div tw="flex flex-col gap-6 w-full h-full">
        {isAnnualSubscription ? (
          <CustomPurchaseCard key="custom-subscription-pricing" type="subscription" />
        ) : (
          <>
            {isFreemiumUser && (
              <SlotPurchaseCard
                type="trial"
                headerTitle={t('trial_header_title')}
                description={t('trial_description')}
                isActive={activeSelectionSubscriptionItem === 'trial'}
                onClick={() => {
                  setActiveSelectionSubscriptionItem('trial');
                  setSlotNumber(1);
                }}
                bundlePrice={99}
                slotPurchaseDescriptions={[
                  `👉 ${t('trial_point_1')}`,
                  `🔑 ${t('trial_point_2')}`,
                  `👩🏻‍ ${t('trial_point_3')}`,
                ]}
              />
            )}
            <CustomPurchaseCard key="custom-flexible-pricing" type="custom" />
            <BundleSlotPurchaseCard
              slotNumber={3}
              isActive={activeSelectionSubscriptionItem === '3bundle'}
              onClick={() => {
                setActiveSelectionSubscriptionItem('3bundle');
                setSlotNumber(3);
              }}
            />
            <BundleSlotPurchaseCard
              slotNumber={6}
              isActive={activeSelectionSubscriptionItem === '6bundle'}
              onClick={() => {
                setActiveSelectionSubscriptionItem('6bundle');
                setSlotNumber(6);
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export const FlexibleSlotPurchaseContent = ({ ...rest }) => {
  const { t } = useTranslation('product-description-page');
  const isFreemiumUser = useIsFreemiumUser();
  const setActiveSelectionSubscriptionItem = useSetAtom(activeSelectionSubscriptionItemAtom);
  const activeSelectionSubscriptionItem = useAtomValue(activeSelectionSubscriptionItemAtom);
  const setSlotNumber = useSetAtom(slotNumberAtom);

  const toggleOrganizationCompanySelect = useSetAtom(onOrganizationCompanySelectToggleAtom);
  const { isAnnualSubscription } = useSubscriptionPurchaseShowcase();
  const { showMessages } = useIntercom();
  const organizationData = useShowOrganizationData();
  const isOrganizationDataLoading = useIsShowOrganisationQueryLoading();
  const calendlyLink = organizationData?.assigned_to_rw_member?.calendly_link;
  const [slotNumber] = useAtom(slotNumberAtom);
  const setIsEmployerNoPermissionToBuySlotModalOpen = useSetAtom(isEmployerNoPermissionToBuySlotModalOpenAtom);
  const router = useV2Router();
  const isAdmin = useIsEmployerAnAdmin();
  const { buySlot } = useBuySlot();

  const onHandleCheckout = React.useCallback(() => {
    if (!isAdmin) {
      setIsEmployerNoPermissionToBuySlotModalOpen(true);
      return;
    }
    if (isAnnualSubscription)
      calendlyLink ? window.open(calendlyLink, '_blank', 'noopener,noreferrer') : showMessages();
    else if (!organizationData?.companies?.length) router.push('/employer/purchase/create-company');
    else if (organizationData?.companies?.length === 1)
      buySlot({ slots: slotNumber, companyId: organizationData?.companies?.[0]?.id });
    else toggleOrganizationCompanySelect(true);
  }, [
    buySlot,
    calendlyLink,
    isAdmin,
    isAnnualSubscription,
    organizationData?.companies,
    router,
    setIsEmployerNoPermissionToBuySlotModalOpen,
    showMessages,
    slotNumber,
    toggleOrganizationCompanySelect,
  ]);

  return (
    <div tw="flex flex-col gap-5">
      {!isFreemiumUser && (
        <div tw="flex flex-row justify-center">
          <SubscriptionPurchaseHeader />
        </div>
      )}
      {isFreemiumUser ? (
        <div tw="w-full grid grid-cols-1 md:grid-cols-2 gap-6 justify-center" {...rest}>
          {/* <div tw="w-fit grid grid-cols-1 md:grid-cols-2 gap-6"> */}
          <SlotPurchaseCard
            type="trial"
            headerTitle={t('trial_header_title')}
            description={t('trial_description')}
            isActive={activeSelectionSubscriptionItem === 'trial'}
            onClick={() => {
              setActiveSelectionSubscriptionItem('trial');
              setSlotNumber(1);
            }}
            bundlePrice={99}
            slotPurchaseDescriptions={[
              `👉 ${t('trial_point_1')}`,
              `🔑 ${t('trial_point_2')}`,
              `👩🏻‍ ${t('trial_point_3')}`,
            ]}
            ctaLabel="Inizia"
            onCtaClick={onHandleCheckout}
            isCtaDisabled={isOrganizationDataLoading || activeSelectionSubscriptionItem !== 'trial'}
            isCtaLoading={isOrganizationDataLoading}
            // ctaStartIcon={LockClosedIcon}
          />

          <CustomPurchaseCard
            key="custom-flexible-pricing-inline"
            type="custom"
            ctaLabel="Inizia"
            onCtaClick={onHandleCheckout}
            isCtaDisabled={isOrganizationDataLoading || activeSelectionSubscriptionItem !== 'custom'}
            isCtaLoading={isOrganizationDataLoading}
          />
        </div>
      ) : !isAnnualSubscription ? (
        <div tw="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-center" {...rest}>
          <CustomPurchaseCard
            key="custom-flexible-pricing-inline"
            type="custom"
            ctaLabel="Inizia"
            onCtaClick={onHandleCheckout}
            isCtaDisabled={isOrganizationDataLoading || activeSelectionSubscriptionItem !== 'custom'}
            isCtaLoading={isOrganizationDataLoading}
          />
          <BundleSlotPurchaseCard
            slotNumber={3}
            isActive={activeSelectionSubscriptionItem === '3bundle'}
            onClick={() => {
              setActiveSelectionSubscriptionItem('3bundle');
              setSlotNumber(3);
            }}
            ctaLabel="Inizia"
            onCtaClick={onHandleCheckout}
            isCtaDisabled={isOrganizationDataLoading || activeSelectionSubscriptionItem !== '3bundle'}
            isCtaLoading={isOrganizationDataLoading}
          />
          <BundleSlotPurchaseCard
            slotNumber={6}
            isActive={activeSelectionSubscriptionItem === '6bundle'}
            onClick={() => {
              setActiveSelectionSubscriptionItem('6bundle');
              setSlotNumber(6);
            }}
            ctaLabel="Inizia"
            onCtaClick={onHandleCheckout}
            isCtaDisabled={isOrganizationDataLoading || activeSelectionSubscriptionItem !== '6bundle'}
            isCtaLoading={isOrganizationDataLoading}
          />
        </div>
      ) : (
        <div tw="w-full flex flex-row justify-center" {...rest}>
          <CustomPurchaseCard
            key="custom-flexible-pricing-inline"
            type="subscription"
            ctaLabel="Contattaci"
            onCtaClick={onHandleCheckout}
            isCtaDisabled={isOrganizationDataLoading || activeSelectionSubscriptionItem !== 'subscription'}
            isCtaLoading={isOrganizationDataLoading}
          />
        </div>
      )}
      <div tw="justify-center items-center inline-flex w-full gap-4">
        <span tw="flex flex-row gap-1 items-center">
          <LockClosedIcon tw="w-4 h-4 text-neutral-200" />
          <Title textColor="neutral-200" fontSize="small" fontWeight="normal" content={t('pay_with_stripe')} />
        </span>
        <div tw="justify-start items-start gap-1 flex">
          {Object.keys(paymentProviderLogoMap).map((v) => (
            <PaymentProviderLogo key={v} paymentProvider={v as keyof typeof paymentProviderLogoMap} />
          ))}
        </div>
      </div>
    </div>
  );
};

export const SubscriptionPurchaseFooter = ({ ...rest }) => {
  const toggleOrganizationCompanySelect = useSetAtom(onOrganizationCompanySelectToggleAtom);
  const { isAnnualSubscription } = useSubscriptionPurchaseShowcase();
  const { showMessages } = useIntercom();
  const { t } = useTranslation('product-description-page');
  const organizationData = useShowOrganizationData();
  const isOrganizationDataLoading = useIsShowOrganisationQueryLoading();
  const calendlyLink = organizationData?.assigned_to_rw_member?.calendly_link;
  const [slotNumber] = useAtom(slotNumberAtom);
  const setIsEmployerNoPermissionToBuySlotModalOpen = useSetAtom(isEmployerNoPermissionToBuySlotModalOpenAtom);
  const router = useV2Router();
  const isAdmin = useIsEmployerAnAdmin();
  const { buySlot } = useBuySlot();

  const onHandleCheckout = React.useCallback(() => {
    if (!isAdmin) {
      setIsEmployerNoPermissionToBuySlotModalOpen(true);
      return;
    }
    if (isAnnualSubscription)
      calendlyLink ? window.open(calendlyLink, '_blank', 'noopener,noreferrer') : showMessages();
    else if (!organizationData?.companies?.length) router.push('/employer/purchase/create-company');
    else if (organizationData?.companies?.length === 1)
      buySlot({ slots: slotNumber, companyId: organizationData?.companies?.[0]?.id });
    else toggleOrganizationCompanySelect(true);
  }, [
    buySlot,
    calendlyLink,
    isAdmin,
    isAnnualSubscription,
    organizationData?.companies,
    router,
    setIsEmployerNoPermissionToBuySlotModalOpen,
    showMessages,
    slotNumber,
    toggleOrganizationCompanySelect,
  ]);

  return (
    <div tw="flex-col justify-start items-center gap-5 inline-flex w-full" {...rest}>
      {!isAnnualSubscription && (
        <div tw="justify-between items-center inline-flex w-full">
          <Title textColor="neutral-200" fontSize="small" fontWeight="normal" content={t('pay_with_stripe')} />
          <div tw="justify-start items-start gap-1 flex">
            {Object.keys(paymentProviderLogoMap).map((v) => (
              <PaymentProviderLogo key={v} paymentProvider={v as keyof typeof paymentProviderLogoMap} />
            ))}
          </div>
        </div>
      )}
      <ButtonV2
        onClick={onHandleCheckout}
        fullwidth
        StartIcon={isAnnualSubscription ? undefined : LockClosedIcon}
        variant="primary"
        disabled={isOrganizationDataLoading}
        loading={isOrganizationDataLoading}
      >
        {isAnnualSubscription ? t('contact_sales_team') : t('secure_checkout')}
      </ButtonV2>

      <EmployerNoPermissionToBuySlotModal />
    </div>
  );
};

const SubscriptionPurchaseShowcaseModal = () => {
  const { showSubscriptionPurchaseShowcase, onClose } = useSubscriptionPurchaseShowcase();
  return (
    <BaseModal
      title={<SubscriptionPurchaseHeader />}
      open={showSubscriptionPurchaseShowcase}
      onClose={onClose}
      modalStyles={[tw`max-w-screen-sm`]}
      footer={<SubscriptionPurchaseFooter />}
      footerStyles={[tw`shadow-[0px_-1px_16px_0px_rgba(0,0,26,0.25)]`]}
    >
      <SubscriptionPurchaseContent tw="mb-6" />
    </BaseModal>
  );
};

export const SubscriptionCheckout = () => {
  const { t } = useTranslation('product-description-page');
  const subscriptionPurchaseShowcaseToggler = useSetAtom(showSubscriptionPurchaseShowcaseAtom);
  return (
    <>
      {/* <div tw="sticky lg:hidden bottom-0 w-full bg-neutral-10 min-h-[6.5rem] px-4 pt-3 pb-4 shadow-[0px_0px_16px_0px_rgba(0,0,26,0.25)] border-t border-neutral-40 flex-col justify-start items-center gap-3 inline-flex">
        <IconButton
          onClick={() => subscriptionPurchaseShowcaseToggler(true)}
          ariaLabel="checkout-panel"
          Icon={ChevronUpIcon}
          variant="neutral"
          size="sm"
        />
        <ButtonV2 onClick={() => subscriptionPurchaseShowcaseToggler(true)} variant="primary" fullwidth>
          {t('see_options')}
        </ButtonV2>
      </div> */}
      <SubscriptionPurchaseShowcaseModal />
      <OrganizationCompanySelectModal />
    </>
  );
};

export default SubscriptionCheckout;
export const onOrganizationCompanySelectToggleAtom = atom<boolean>(false);

export const OrganizationCompanySelectModal = () => {
  const organizationData = useShowOrganizationData();
  const companies = React.useMemo(
    () =>
      organizationData?.companies?.map((v) => ({
        label: `${v.name} ${v.vat_number === 'ITnull' ? '' : `(${v.vat_number})`}`,
        value: v.id,
        id: v.id,
        key: v.id,
      })),
    [organizationData?.companies]
  );
  const [open, setOpen] = useAtom(onOrganizationCompanySelectToggleAtom);
  const [slotNumber] = useAtom(slotNumberAtom);
  const methods = useForm<{
    companyId: string;
  }>({
    defaultValues: { companyId: '' },
  });
  const { buySlot } = useBuySlot();

  const onHandleCheckout = methods.handleSubmit((values) => {
    buySlot({ slots: slotNumber, companyId: values.companyId });
  });

  React.useEffect(() => {
    methods.reset({ companyId: '' });
  }, [methods]);

  const { t } = useTranslation('product-description-page');
  return (
    <BaseModal
      footer={
        <ButtonV2 fullwidth onClick={onHandleCheckout} variant="primary" StartIcon={LockClosedIcon}>
          <Title textColor="purple-500" fontSize="base" fontWeight="semibold">
            {t('secure_checkout')}
          </Title>
        </ButtonV2>
      }
      footerStyles={[tw`pt-[5rem]`]}
      open={open}
      onClose={() => {
        setOpen(false);
        methods.reset({ companyId: '' });
      }}
    >
      <div tw="flex gap-8 flex-col">
        <Title
          isCenter
          twStyle={tw`leading-[30px]`}
          textColor="neutral-700"
          fontSize="extraLarge"
          fontWeight="semibold"
          content={t('select_a_company')}
        />
        <Title
          textColor="neutral-700"
          fontSize="base"
          fontWeight="medium"
          content={t('we_will_invoice_your_purchase_to_the_company_you_chose')}
          twStyle={tw`leading-none`}
        />
        <form action="post" method="post" onSubmit={onHandleCheckout}>
          <Controller
            control={methods.control}
            name="companyId"
            render={({ field }) => <RadioGroup options={companies} id="company_id" {...field} />}
            rules={{ required: 'Company is required!' }}
          />
        </form>
      </div>
    </BaseModal>
  );
};
